/**
 * Small pieces shared by the admin screens. No vocabulary lives here: callers
 * pass in already-resolved words, so there is nowhere in this file for a
 * hardcoded display term to hide.
 */
import type { ReactNode } from 'react';
import { Dialog } from '../versions/Dialog';

/** An error the person must notice. `role="alert"` announces it without a hunt. */
export function FormError({ children }: { children: ReactNode }) {
  if (!children) return null;
  return (
    <div className="status err adm-error" role="alert">
      {children}
    </div>
  );
}

/** A list of the API's own reasons, e.g. every password rule that was broken. */
export function ProblemList({ problems }: { problems: string[] }) {
  if (problems.length === 0) return null;
  return (
    <ul className="adm-problems" role="alert">
      {problems.map((p) => (
        <li key={p}>{p}</li>
      ))}
    </ul>
  );
}

/**
 * Confirmation for a destructive or far-reaching action. The body must NAME what
 * is affected — the caller supplies it — and the confirm button carries the verb
 * ("Deactivate Ada"), never a bare "OK".
 */
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  busy,
  error,
  danger,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  body: ReactNode;
  confirmLabel: string;
  busy: boolean;
  error: ReactNode;
  danger?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <Dialog open={open} title={title} onClose={onClose} dismissible={!busy}>
      {body}
      <FormError>{error}</FormError>
      <div className="bp-dialog-actions">
        <button type="button" className="btn" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button
          type="button"
          className={danger ? 'btn danger' : 'btn primary'}
          onClick={onConfirm}
          disabled={busy}
        >
          {busy ? 'Working…' : confirmLabel}
        </button>
      </div>
    </Dialog>
  );
}

export function formatWhen(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** A lowercase, hyphenated suggestion for a slug field. The person may overwrite it. */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}

export const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
